export interface OauthPopupResult {
    ok: boolean;
    code?: string;
    state?: string;
    /** True for admin-consent flows that complete with `admin_consent=True` (no code). */
    adminConsentGranted?: boolean;
    error?: string;
    /** Provider-supplied subcode (e.g. Microsoft's `error_subcode=cancel`) for branching UX. */
    errorSubcode?: string;
    cancelled?: boolean;
}

export type OauthMessage =
    | { type: 'oauth:complete'; code: string; state: string }
    | { type: 'oauth:admin-consent-granted'; state: string }
    | { type: 'oauth:error'; error: string; errorSubcode?: string };

export interface OauthPopupHandle {
    result: Promise<OauthPopupResult>;
    close: () => void;
}

const POPUP_FEATURES = 'width=600,height=720,menubar=no,toolbar=no,location=no,status=no';

export function openOauthPopup(url: string): OauthPopupHandle {
    const popup = window.open(url, 'integrations-oauth', POPUP_FEATURES);
    if (!popup) {
        return {
            result: Promise.resolve({
                ok: false,
                error: 'Popup was blocked — please allow popups for this site and try again.',
            }),
            close: (): void => {},
        };
    }

    let settled = false;
    let cancelledByCaller = false;

    const result = new Promise<OauthPopupResult>((resolve) => {
        const settle = (r: OauthPopupResult): void => {
            if (settled) return;
            settled = true;
            window.removeEventListener('message', messageHandler);
            window.clearInterval(pollTimer);
            resolve(r);
        };

        const messageHandler = (event: MessageEvent): void => {
            if (event.source !== popup) return;
            if (event.origin !== window.location.origin) return;
            const data = event.data as OauthMessage | undefined;
            if (!data || typeof data !== 'object') return;
            if (data.type === 'oauth:complete') {
                settle({ ok: true, code: data.code, state: data.state });
            } else if (data.type === 'oauth:admin-consent-granted') {
                settle({ ok: true, adminConsentGranted: true, state: data.state });
            } else if (data.type === 'oauth:error') {
                const result: OauthPopupResult = { ok: false, error: data.error };
                if (data.errorSubcode !== undefined) result.errorSubcode = data.errorSubcode;
                settle(result);
            }
        };

        const pollTimer = window.setInterval(() => {
            if (!popup.closed) return;
            if (cancelledByCaller) settle({ ok: false, cancelled: true });
            else settle({ ok: false, error: 'Authorization window was closed before completion.' });
        }, 500);

        window.addEventListener('message', messageHandler);
    });

    return {
        result,
        close: (): void => {
            cancelledByCaller = true;
            if (!popup.closed) popup.close();
        },
    };
}
